import { Button } from '@ps/isp-web-component-library';
import type { MarketId, MarketTemplate, MarketView } from '@ps/isp-web-services-types';
import { FeatureFlag } from '@ps/web-core-enums';
import type { TranslationTag } from '@ps/web-core-intl';
import type { EventPageTabEntities } from '@ps/web-shared-analytics';
import { Translation, useTrackingLabel } from '@ps/web-shared-components';
import { isEmpty, noop } from 'lodash';
import React, { useEffect, useState } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import Redirect from '@swf/components/Redirect/Redirect';
import useFeature from '@swf/core/hooks/useFeature';
import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import { useWidget } from '@swf/core/hooks/useWidget';
import { select } from '@swf/core/store';
import { MarketTemplatePanel, Message } from '@swf/sports/components';
import LiveDataWarning from '@swf/sports/components/LiveDataWarning/LiveDataWarning';
import MarketGroupTabs from '@swf/sports/components/MarketGroupTabs/MarketGroupTabs';
import { translationMap } from '@swf/sports/components/MarketTemplate/MarketTemplatePanel';
import { Search } from '@swf/sports/components/Search/Search';
import PopularBetBuilders from '@swf/sports/features';
import { isCompoundMarketTemplate } from '@swf/sports/utils/isCompoundMarketTemplate';

import { useSportsEventPageWidget } from '../../../../model';
import type { BetBuilderMarketGroup, ClientMarketGroup } from '../../../../model/types';
import useDisplayableMarketGroups from '../../hooks/useDisplayableMarketGroups';

import {
  TXTCLI_Clear,
  TXTWCL_Find_market_by_nameXX,
  TXTWCL_No_results_found_for_N0,
  TXTWCL_There_are_no_markets_available_ELL
} from './MarketGroups.messages.json';
import styles from './MarketGroups.scss';

const numberOfInitiallyOpenMarkets = 5;

const isBetBuilderMarketGroup = (group: ClientMarketGroup): group is BetBuilderMarketGroup =>
  group.type === 'BET_BUILDER';

const getMarketTemplateSortValue = (
  {
    template,
    markets
  }: {
    template: MarketTemplate;
    markets: Record<MarketId, MarketView>;
  },
  formatMessageFn: (translationValue: TranslationTag | MessageDescriptor) => string
) => {
  const isTemplateCompound = isCompoundMarketTemplate(template);
  const titleInTranslationMap = isTemplateCompound && translationMap[template.config.title];
  return isCompoundMarketTemplate(template)
    ? titleInTranslationMap
      ? formatMessageFn(titleInTranslationMap)
      : ''
    : markets[template.marketId].marketName;
};

const Markets: React.FC = () => {
  const isEventPagePopularBetBuildersEnabled = useFeature(
    FeatureFlag.ISPEnableEventPagePopularBetBuilders
  );
  const { widgetSelect: marketsWidgetSelect } = useSportsEventPageWidget();
  const { widgetSelect } = useWidget();

  const { eventTypeSlug, eventTypeId, competitionSlug, competitionId, eventSlug, eventId } =
    useSelector(select.sports.getUrlParams);

  const config = {
    eventTypeSlug,
    eventTypeId,
    competitionSlug,
    competitionId,
    eventSlug,
    eventId
  };

  const markets = useSelector(marketsWidgetSelect.markets) as Record<MarketId, MarketView>;
  const marketGroups = useSelector(marketsWidgetSelect.marketsFeature);
  const locale = useSelector(select.locale.get);
  const hasInPlayMarkets = useSelector(widgetSelect?.hasInPlayMarkets ?? noop);
  const { hash } = useLocation();
  const eventStartTime = !isEmpty(markets) ? Object.values(markets)[0].marketTime : '';

  const [marketGroupSlug, setMarketGroupSlug] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const { formatMessage } = useRegulatoryIntl();

  const [displayableMarketGroups, currentGroup] = useDisplayableMarketGroups({
    markets,
    groups: marketGroups,
    currentMarketGroupSlug: marketGroupSlug
  });

  const searchTrackingLabel = useTrackingLabel(TXTWCL_Find_market_by_nameXX);

  const eventPageTabEntities: EventPageTabEntities = {
    eventId: Number(eventId),
    sportId: Number(eventTypeId),
    competitionId: Number(competitionId),
    eventStartTime
  };

  useEffect(() => {
    setSearchTerm('');
  }, [marketGroupSlug]);

  useEffect(() => {
    setMarketGroupSlug(hash.replace('#', ''));
  }, [hash]);

  if (!displayableMarketGroups.length) {
    return <Message message={<Translation {...TXTWCL_There_are_no_markets_available_ELL} />} />;
  } else if (!currentGroup) {
    return (
      <Redirect
        alias="sportsEvent"
        values={{
          ...config,
          marketGroup: undefined
        }}
      />
    );
  } else if (!currentGroup.templates.length) {
    return (
      <>
        <MarketGroupTabs
          groups={displayableMarketGroups}
          routeValues={config}
          marketGroupSlug={marketGroupSlug}
          entities={eventPageTabEntities}
        />
        <Message message={<Translation {...TXTWCL_There_are_no_markets_available_ELL} />} />
      </>
    );
  }

  const onSearchTermChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const onSearchTermClear = () => setSearchTerm('');

  const lowerSearchTerm = searchTerm.toLowerCase();

  const templates = lowerSearchTerm
    ? currentGroup.templates.filter((template) => {
        if (isCompoundMarketTemplate(template)) {
          // TODO: This needs to test the translated text rather than the token
          return template.config.title.toLowerCase().includes(lowerSearchTerm);
        }
        return markets[template.marketId].marketName.toLowerCase().includes(lowerSearchTerm);
      })
    : currentGroup.templates;

  const sortedTemplates =
    currentGroup.type === 'ALL_MARKETS'
      ? templates.sort((a, b) =>
          getMarketTemplateSortValue({ template: a, markets }, formatMessage).localeCompare(
            getMarketTemplateSortValue({ template: b, markets }, formatMessage),
            locale,
            { sensitivity: 'base' }
          )
        )
      : templates;

  const initiallyOpenedPanels = sortedTemplates
    .slice(0, numberOfInitiallyOpenMarkets)
    .map((template) =>
      isCompoundMarketTemplate(template) ? template.marketIds[0] : template.marketId
    );

  return (
    <>
      <MarketGroupTabs
        groups={displayableMarketGroups}
        routeValues={config}
        marketGroupSlug={marketGroupSlug}
        entities={eventPageTabEntities}
      />

      {currentGroup.type === 'ALL_MARKETS' && (
        <div className={styles.searchWrapper}>
          <Search
            onChange={onSearchTermChange}
            onClear={onSearchTermClear}
            searchTerm={searchTerm}
            trackingLabel={searchTrackingLabel}
            placeholderTranslation={TXTWCL_Find_market_by_nameXX}
          />
        </div>
      )}

      {sortedTemplates.length ? (
        <>
          {isEventPagePopularBetBuildersEnabled &&
          isBetBuilderMarketGroup(currentGroup) &&
          currentGroup.popularBetBuilders.length > 0 ? (
            <div className={styles.betBuilderWrapper}>
              <PopularBetBuilders
                popularBetBuilders={currentGroup.popularBetBuilders}
                isEventPage
              />
            </div>
          ) : null}

          {sortedTemplates.map((template) => {
            const activeMarketId = isCompoundMarketTemplate(template)
              ? template.marketIds[0]
              : template.marketId;

            return (
              <MarketTemplatePanel
                key={`${marketGroupSlug ? `${marketGroupSlug}-` : ''}${activeMarketId}`}
                openInitially={initiallyOpenedPanels.includes(activeMarketId)}
                highlight={searchTerm}
                {...template}
              />
            );
          })}

          {hasInPlayMarkets ? <LiveDataWarning /> : null}
        </>
      ) : (
        <div className={styles.afterMarkets}>
          <p className={styles.message}>
            <Translation values={{ 0: searchTerm }} {...TXTWCL_No_results_found_for_N0} />
          </p>

          <div className={styles.buttonWrapper}>
            <Button onClick={onSearchTermClear}>
              <Translation {...TXTCLI_Clear} />
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default Markets;
